import { useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { FuentePatroclo, PasoPatroclo } from '@manejo-alcanza/contracts';
import { TextoRespuesta } from './TextoRespuesta';
import { NOMBRES_NODO, NOMBRES_TOOL } from './nombres';

/**
 * Una respuesta de Patroclo: el texto, de dónde salió cada dato y el
 * recorrido por el grafo.
 *
 * Portado de Patroclo-Local con tres cambios (traspaso §6):
 *
 * 1. **Las fuentes según su `tipo`.** Un `documento` es un fragmento de la base
 *    de conocimiento; una `tool` es una consulta a ManejoAlcanza, y se enseña la
 *    consulta con sus parámetros y del sobre lo que no puede perderse: el
 *    período y las advertencias. Una cifra sin sus advertencias es justo lo que
 *    el contrato existe para evitar.
 * 2. **Los nodos del grafo nuevo.**
 * 3. **El `id_ejecucion`, discreto**: es lo que alguien dicta cuando una
 *    respuesta le parece rara, y con él se abre su traza.
 */

export function MensajeAsistente({
  contenido,
  fuentes,
  pasos,
  rechazada,
  tiempoMs,
  idEjecucion,
}: {
  contenido: string;
  fuentes: FuentePatroclo[];
  pasos: PasoPatroclo[];
  rechazada: boolean;
  tiempoMs: number | null;
  /** Solo en las respuestas de esta sesión: el historial no lo guarda. */
  idEjecucion?: string;
}): ReactNode {
  const [fuentesAbiertas, setFuentesAbiertas] = useState(false);
  const [resaltada, setResaltada] = useState<number | null>(null);
  const refFuentes = useRef<HTMLDivElement>(null);

  const verFuente = (numero: number): void => {
    setFuentesAbiertas(true);
    setResaltada(numero);
    requestAnimationFrame(() =>
      refFuentes.current
        ?.querySelector(`[data-fuente="${numero}"]`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }),
    );
  };

  return (
    <div
      className={`max-w-[46rem] space-y-3 rounded-2xl rounded-bl-md border p-3 shadow-suave sm:p-4 ${
        rechazada ? 'border-aviso/30 bg-aviso-tenue' : 'border-borde bg-superficie'
      }`}
    >
      {/*
        Un rechazo es una respuesta normal, no un error: fuera de tema, sin
        información, una acción que todavía no se puede hacer. Ámbar, como
        «requiere revisión», no rojo.
      */}
      {rechazada && <div className="text-xs font-semibold uppercase tracking-wide text-aviso">No respondida</div>}

      <TextoRespuesta texto={contenido} totalFuentes={fuentes.length} alCitar={verFuente} />

      {fuentes.length > 0 && (
        <details open={fuentesAbiertas} onToggle={(e) => setFuentesAbiertas(e.currentTarget.open)}>
          <summary className="flex min-h-[44px] cursor-pointer items-center text-xs font-medium text-tenue sm:min-h-0">
            Fuentes ({fuentes.length})
          </summary>
          <div ref={refFuentes} className="mt-2 space-y-2">
            {fuentes.map((f) => (
              <div
                key={f.numero}
                data-fuente={f.numero}
                className={`rounded-md border p-2 text-sm ${
                  resaltada === f.numero ? 'border-acento bg-acento-tenue' : 'border-borde'
                }`}
              >
                {f.tipo === 'tool' ? <FuenteConsulta fuente={f} /> : <FuenteDocumento fuente={f} />}
              </div>
            ))}
          </div>
        </details>
      )}

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-tenue">
        {pasos.length > 0 && (
          <details className="w-full">
            <summary className="flex min-h-[44px] cursor-pointer items-center sm:min-h-0">
              Recorrido{tiempoMs !== null && ` · ${segundos(tiempoMs)}`}
            </summary>
            <ol className="mt-2 space-y-1">
              {pasos.map((p, i) => (
                <li key={i} className="rounded border border-borde bg-fondo px-2 py-1">
                  <div className="flex items-baseline gap-2">
                    <span className="font-medium text-texto">{NOMBRES_NODO[p.nodo] ?? p.nodo}</span>
                    <code className="text-[0.7rem]">{p.nodo}</code>
                    <span className="cifra ml-auto">{p.ms >= 1000 ? segundos(p.ms) : `${p.ms} ms`}</span>
                  </div>
                  {p.detalle && <div className="mt-0.5 break-words">{p.detalle}</div>}
                </li>
              ))}
            </ol>
          </details>
        )}
        {idEjecucion && (
          /*
           * Abre la traza de esta respuesta (§6.1). Hoy la pestaña entera es de
           * administrador y desarrollador, los mismos que ven las trazas; el
           * día que el chat se abra a otros, esto se vuelve texto para ellos
           * (el proxy ya les contestaría 403).
           */
          <Link
            to={`/patroclo/trazas/${idEjecucion}`}
            title={`Ver la traza de esta respuesta: ${idEjecucion}`}
            className="font-mono underline decoration-dotted hover:text-acento"
          >
            ref. {idEjecucion.slice(0, 8)}
          </Link>
        )}
      </div>
    </div>
  );
}

function FuenteDocumento({ fuente: f }: { fuente: FuentePatroclo }): ReactNode {
  return (
    <>
      <div className="flex items-baseline gap-2">
        <Numero n={f.numero} />
        <span className="font-medium">{f.documento ?? f.archivo ?? 'Documento'}</span>
        {typeof f.similitud === 'number' && (
          <span className="cifra ml-auto text-xs text-tenue" title="Qué tanto se parece a la pregunta">
            {Math.round(f.similitud * 100)} %
          </span>
        )}
      </div>
      {(f.seccion || f.pagina) && (
        <div className="mt-0.5 text-xs text-tenue">
          {f.seccion && <>Sección: {f.seccion}</>}
          {f.seccion && f.pagina ? ' · ' : ''}
          {f.pagina ? <>Página {f.pagina}</> : null}
        </div>
      )}
      {f.fragmento && (
        <blockquote className="mt-1 whitespace-pre-wrap border-l-2 border-borde pl-2 text-tenue">
          {sinLineaDeContexto(f.fragmento)}
        </blockquote>
      )}
    </>
  );
}

/**
 * Una consulta a ManejoAlcanza: qué se pidió, con qué parámetros, y del sobre
 * el período, cuánto se miró y las advertencias. Los datos completos quedan a
 * un clic: son agregados, no filas.
 */
function FuenteConsulta({ fuente: f }: { fuente: FuentePatroclo }): ReactNode {
  const sobre = (f.resultado ?? {}) as {
    periodo?: { desde?: string | null; hasta?: string };
    muestra?: Record<string, number>;
    sin_recepcion_registrada?: number | null;
    advertencias?: string[];
    datos?: unknown;
  };
  const parametros = Object.entries(f.parametros ?? {});

  return (
    <>
      <div className="flex items-baseline gap-2">
        <Numero n={f.numero} />
        <span className="font-medium">
          Consulta a ManejoAlcanza: {NOMBRES_TOOL[f.tool ?? ''] ?? f.tool ?? 'desconocida'}
        </span>
      </div>
      {parametros.length > 0 && (
        <div className="mt-1 flex flex-wrap gap-1">
          {parametros.map(([k, v]) => (
            <span key={k} className="rounded bg-neutro-tenue px-1.5 py-0.5 font-mono text-[0.7rem] text-neutro">
              {k}: {String(v)}
            </span>
          ))}
        </div>
      )}
      <div className="mt-1 text-xs text-tenue">
        {sobre.periodo?.hasta && (
          <>
            Período: {sobre.periodo.desde ? `${sobre.periodo.desde} a ${sobre.periodo.hasta}` : `al ${sobre.periodo.hasta}`}
          </>
        )}
        {sobre.muestra && (
          <>
            {' · '}
            {/* «ordenes: 1» y no «1 ordenes»: las claves son las del contrato, sin plural. */}
            {Object.entries(sobre.muestra)
              .map(([k, v]) => `${k}: ${v}`)
              .join(', ')}
          </>
        )}
        {typeof sobre.sin_recepcion_registrada === 'number' && sobre.sin_recepcion_registrada > 0 && (
          <> · {sobre.sin_recepcion_registrada} líneas sin recepción registrada</>
        )}
      </div>
      {sobre.advertencias && sobre.advertencias.length > 0 && (
        <ul className="mt-1 list-disc space-y-0.5 pl-5 text-xs text-aviso">
          {sobre.advertencias.map((a, i) => (
            <li key={i}>{a}</li>
          ))}
        </ul>
      )}
      {sobre.datos !== undefined && (
        <details className="mt-1">
          <summary className="cursor-pointer text-xs text-tenue">Ver los datos que devolvió</summary>
          <pre className="mt-1 max-h-64 overflow-auto rounded bg-fondo p-2 text-[0.7rem]">
            {JSON.stringify(sobre.datos, null, 2)}
          </pre>
        </details>
      )}
    </>
  );
}

function Numero({ n }: { n: number }): ReactNode {
  return (
    <span className="inline-flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-acento-tenue px-1 text-[0.7rem] font-semibold text-acento">
      {n}
    </span>
  );
}

function segundos(ms: number): string {
  return `${(ms / 1000).toLocaleString('es-CO', { maximumFractionDigits: 1 })} s`;
}

/** Los fragmentos empiezan con «Documento: …» para el modelo; aquí el nombre ya está arriba. */
function sinLineaDeContexto(texto: string): string {
  return texto.replace(/^Documento: .*\n\n/, '');
}
